import AppPagination from "@/component/common/AppPagination.tsx";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import CustomRequiredMarkAntdForm from "@/component/CustomRequiredMarkAntdForm.tsx";
import { LICENSE_REGISTRATION_ENUM } from "@/constant/constants.ts";
import { DateHelper, StringHelper } from "@/constant/helper.ts";
import {
    LICENSE_REGISTRATION_SENT_STATUS_OPTION,
    LICENSE_REGISTRATION_STATUS_OPTION,
} from "@/constant/options.ts";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { LicenseRegistrationService } from "@/services/licenseRegistration/LicenseRegistrationService.ts";
import { toast } from "@/utils/toast.tsx";
import { LicenseRegistrationTableHeader } from "@/view/license/component/LicenseRegistrationTableHeader.tsx";
import { PlusOutlined } from "@ant-design/icons";
import {
    Button,
    DatePicker,
    Flex,
    Form,
    Select,
    Table,
    TablePaginationConfig,
    TableProps,
} from "antd";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

type SearchFormType = {
  status?: number | null;
  sentStatus?: number | null;
  registrationDate?: Date[] | null;
};

const LicenseRegistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isFirstRun = useRef<boolean>(true);
  const [dataTable, setDataTable] = useState<LicenseRegistrationItemSearch[]>(
    [],
  );
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [pagination, setPagination] = useState<PaginationAndSort>({
    size: 10,
    page: 0,
    sortBy: ["created_date"],
    sortDesc: [true],
  });
  const [searchForm] = Form.useForm<SearchFormType>();

  const handleSearch = async () => {
    const { registrationDate } = searchForm.getFieldsValue();
    const formattedDates = registrationDate
      ? registrationDate.map((date: Date) =>
          DateHelper.convertDateToStringDate(date),
        )
      : null;
    const request: SearchLicenseRegistrationListRequest = {
      ...searchForm.getFieldsValue(),
      ...pagination,
      registrationDate: formattedDates,
    };
    const res: SearchLicenseRegistrationResponse | null | undefined =
      await LicenseRegistrationService.search(request);

    if (!res?.items || res?.items?.length === 0) {
      setTotalRecord(res?.total || 0);
      setDataTable([]);
      if (pagination.page > 0) {
        onChangePageNum(1);
        return;
      }
      // If backend explicitly returns total: 0, don't show "no results" warning
      if (!isFirstRun.current && (res?.total ?? 0) === 0) {
        toast.warning(t("message.noResultsFound"));
      }
      return;
    }
    setTotalRecord(res?.total || 0);
    setDataTable(res?.items || []);
  };

  const columns: TableProps<ProductDetail>["columns"] =
    LicenseRegistrationTableHeader(t, handleSearch);

  const onChangePageNum = (page: number) => {
    setPagination((prevState) => ({
      ...prevState,
      page: page - 1,
    }));
  };

  const onChangePageSize = (pageSize: number) => {
    setPagination((prevState) => ({
      ...prevState,
      page: 0,
      size: pageSize,
    }));
  };

  const onClickAddNewLicenseRegistration = () => {
    navigate(MANAGER_PATH.LICENSE_REGISTRATION.ADD);
  };

  const onChangeTable = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<LicenseRegistrationItemSearch>
      | SorterResult<LicenseRegistrationItemSearch>[],
  ) => {
    const sortValue = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!sortValue.column) {
      setPagination((prevState) => ({
        ...prevState,
        page: 0,
        sortBy: ["created_date"],
        sortDesc: [true],
      }));
    } else {
      setPagination((prevState) => ({
        ...prevState,
        page: 0,
        sortDesc: [sortValue.order === "descend"],
        sortBy: [StringHelper.toSnakeCase(String(sortValue.columnKey))],
      }));
    }
  };

  useEffect(() => {
    if (location.state?.from === "dashboard-expiringSoonBlock") {
      searchForm.setFieldValue(
        "status",
        LICENSE_REGISTRATION_ENUM.ALMOST_EXPIRED,
      );
    } else {
      handleSearch().finally(() => {
        if (isFirstRun.current) {
          isFirstRun.current = false;
        }
      });
    }

    navigate(location.pathname, { replace: true });
  }, [pagination, location.state?.from]);

  return (
    <>
      <ManageContentLayout
        headerFunction={
          <>
            <Button
              icon={<PlusOutlined />}
              type={"primary"}
              size={"large"}
              onClick={() => onClickAddNewLicenseRegistration()}
            >
              {t("button.addLicenseRegistration")}
            </Button>
          </>
        }
      >
        <div
          className={"p-3 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          <Form
            form={searchForm}
            name="search-form"
            layout={"vertical"}
            colon={false}
            requiredMark={CustomRequiredMarkAntdForm}
            onFinish={() => onChangePageNum(1)}
            size={"large"}
          >
            <Flex justify={"flex-start"} align={"center"} gap={24}>
              <Form.Item<SearchFormType>
                name="status"
                className={"m-1 w-[200px]"}
              >
                <Select
                  placeholder={t("placeholder.selectStatus")}
                  options={LICENSE_REGISTRATION_STATUS_OPTION}
                  allowClear
                />
              </Form.Item>
              <Form.Item<SearchFormType>
                name="sentStatus"
                className={"m-1 w-[200px]"}
              >
                <Select
                  placeholder={t("placeholder.selectSentStatus")}
                  options={LICENSE_REGISTRATION_SENT_STATUS_OPTION}
                  allowClear
                />
              </Form.Item>
              <Form.Item<SearchFormType>
                name="registrationDate"
                label={t("field.registrationDate")}
                layout={"horizontal"}
                className={"m-1"}
              >
                <DatePicker.RangePicker className={"w-[255px]"} />
              </Form.Item>
              <Form.Item className={"text-end m-1"}>
                <Button type="primary" htmlType="submit" className="m-1">
                  {t("button.search")}
                </Button>
              </Form.Item>
            </Flex>
          </Form>

          {/* Table function block */}
          <div className={"mt-5"}>
            <Table
              rowKey={"licenseRegistrationId"}
              columns={columns}
              dataSource={dataTable}
              pagination={false}
              onChange={onChangeTable}
            />
            <AppPagination
              className={"mt-3"}
              pageSize={pagination.size}
              onChangePageSize={onChangePageSize}
              onChangePage={onChangePageNum}
              currentPage={pagination.page}
              total={totalRecord}
            />
          </div>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default LicenseRegistration;
